import axios from '../config/axiosConfig';
import riskScoringService from './riskScoringService';

const epssService = {
  // Get EPSS score for a single CVE
  async getEPSSScore(cveId) {
    try {
      const response = await axios.get(`/epss/cve/${cveId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching EPSS score:', error);
      throw error;
    }
  },

  // Get EPSS scores for multiple CVEs
  async getBulkEPSSScores(cveIds) {
    try {
      const response = await axios.post(`/epss/bulk`, { cves: cveIds });
      return response.data;
    } catch (error) {
      console.error('Error fetching bulk EPSS scores:', error);
      throw error;
    }
  },

  // Get EPSS statistics for a project
  async getProjectEPSSStats(projectId) {
    try {
      const response = await axios.get(`/epss/project/${projectId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching project EPSS stats:', error);
      throw error;
    }
  },

  // Calculate risk score with EPSS integration
  calculateEnhancedRisk(action, targets, epssData) {
    // Get base risk score from existing service
    const baseRisk = riskScoringService.calculateActionRisk(action, targets);
    
    // Enhance with EPSS data if available
    if (epssData && Object.keys(epssData).length > 0) {
      const epssScores = Object.values(epssData).map(d => d.epss);
      const maxEpss = Math.max(...epssScores);
      const avgEpss = epssScores.reduce((a, b) => a + b, 0) / epssScores.length;
      
      // Adjust risk score based on EPSS
      let epssMultiplier = 1;
      if (maxEpss > 0.9) epssMultiplier = 1.5;
      else if (maxEpss > 0.5) epssMultiplier = 1.3;
      else if (maxEpss > 0.1) epssMultiplier = 1.1;
      
      const enhancedScore = Math.min(10, baseRisk.score * epssMultiplier);
      
      return {
        ...baseRisk,
        score: parseFloat(enhancedScore.toFixed(1)),
        epssData: {
          maxEpss: maxEpss.toFixed(3),
          avgEpss: avgEpss.toFixed(3),
          highRiskCves: epssScores.filter(s => s > 0.5).length
        },
        factors: [
          ...baseRisk.factors,
          {
            factor: 'EPSS Score',
            weight: 0.3,
            value: maxEpss,
            detail: `Max EPSS: ${(maxEpss * 100).toFixed(1)}% exploitation probability`
          }
        ]
      };
    }
    
    return baseRisk;
  },

  // Get risk level based on EPSS score
  getEPSSRiskLevel(epssScore) {
    if (epssScore >= 0.9) return { level: 'Critical', color: 'red' };
    if (epssScore >= 0.5) return { level: 'High', color: 'orange' };
    if (epssScore >= 0.1) return { level: 'Medium', color: 'yellow' };
    if (epssScore >= 0.01) return { level: 'Low', color: 'green' };
    return { level: 'Very Low', color: 'gray' };
  },

  // Format EPSS data for display
  formatEPSSDisplay(epssData) {
    if (!epssData) return 'N/A';
    
    const riskLevel = this.getEPSSRiskLevel(epssData.epss);
    return {
      score: epssData.epss.toFixed(3),
      percentile: epssData.percentile.toFixed(1),
      probability: `${(epssData.epss * 100).toFixed(2)}%`,
      riskLevel: riskLevel.level,
      color: riskLevel.color,
      lastUpdated: epssData.date
    };
  }
};

export default epssService;